import { Link } from "react-router-dom";
import { Check, ArrowRight } from "lucide-react";
import Seo from "@/components/Seo";
import PageHero from "@/components/PageHero";
import CTASection from "@/components/CTASection";
import BookingPlatforms from "@/components/sections/BookingPlatforms";
import { Button } from "@/components/ui/button";
import { RevealGroup, RevealItem, Reveal } from "@/components/Reveal";
import { Icon } from "@/components/Icon";
import { useAssessment } from "@/context/AssessmentContext";
import { detailedServices, IMG } from "@/data/content";

export default function Services() {
  const { openAssessment } = useAssessment();
  return (
    <>
      <Seo title="Hospitality Services | HM Services" description="Booking platform setup, listing optimization, digital marketing, reputation management and hospitality consulting for hotels, guest houses, resorts and villas." path="/services" image={IMG.room} />
      <PageHero eyebrow="Our Services" title="Hospitality Solutions Built Around Your Property" subtitle="Every property is different. HM Services provides practical digital and hospitality solutions based on your property's size, location, target guests and business goals." image={IMG.room}>
        <Button onClick={openAssessment} data-testid="services-hero-cta" className="rounded-full bg-sky px-8 py-6 text-base font-semibold text-navy hover:bg-sky-light">
          Discuss Your Property
        </Button>
      </PageHero>

      <section className="bg-white py-24 lg:py-32" data-testid="detailed-services">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <RevealGroup className="grid gap-6 md:grid-cols-2">
            {detailedServices.map((s, i) => (
              <RevealItem key={s.title}>
                <div
                  data-testid={`detailed-service-${i}`}
                  className="flex h-full flex-col rounded-2xl border border-line bg-white p-8 shadow-float transition-[transform,box-shadow] duration-300 hover:-translate-y-1.5 hover:shadow-float-lg"
                >
                  <div className="flex items-center gap-4">
                    <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-navy text-sky">
                      <Icon name={s.icon} className="h-5 w-5" />
                    </span>
                    <h3 className="font-serif text-xl font-semibold text-navy">{s.title}</h3>
                  </div>
                  <p className="mt-4 text-sm leading-relaxed text-slate-600">{s.desc}</p>
                  {s.bullets && (
                    <ul className="mt-5 grid grid-cols-1 gap-2 sm:grid-cols-2">
                      {s.bullets.map((b) => (
                        <li key={b} className="flex items-center gap-2 text-sm text-slate-700">
                          <Check className="h-4 w-4 shrink-0 text-ocean" />{b}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </RevealItem>
            ))}
          </RevealGroup>

          <Reveal className="mt-14 text-center">
            <Button asChild variant="outline" className="rounded-full border-navy/20 px-8 py-6 text-base text-navy hover:bg-navy hover:text-white" data-testid="services-packages-link">
              <Link to="/packages">View Our Packages <ArrowRight className="h-4 w-4" /></Link>
            </Button>
          </Reveal>
        </div>
      </section>

      <BookingPlatforms />
      <CTASection />
    </>
  );
}
